/**
 * SCORM 1.2 Player component — renders SCORM content in an iframe.
 * Independent — no content-core dependency. Supports i18n via `language` prop.
 */

import React, { useMemo } from 'react';
import { ScormPlayerFrame } from '../ScormPlayerFrame';
import type { ScormLaunchConfig, ScormCompletionEvent } from '../types';
import { useScormTranslations } from '../i18n';
import type { Scorm12Data, Scorm12Answer } from './scorm12-schema';

export interface Scorm12PlayerProps {
    /** Content data. */
    data: Scorm12Data;
    /** Session token for SCORM runtime API calls. */
    sessionToken?: string;
    /** Session ID. */
    sessionId?: string;
    /** Learner name. */
    learnerName?: string;
    /** Learner ID. */
    learnerId?: string;
    /** Called when the learner answers / completes. */
    onAnswer?: (answer: Scorm12Answer) => void;
    /** UI language code (e.g. 'en', 'vi'). */
    language?: string;
    /** Whether interaction is disabled. */
    disabled?: boolean;
}

export function Scorm12Player({
    data: rawData,
    sessionToken,
    sessionId,
    learnerName,
    learnerId,
    onAnswer,
    language,
}: Scorm12PlayerProps) {
    const data = rawData ?? ({} as Scorm12Data);
    const t = useScormTranslations(language);

    // Normalize data keys — accept both camelCase (FE schema) and snake_case (API response)
    const rawDataAny = data as Record<string, unknown>;
    const packageId = data.packageId || (rawDataAny.package_id as string);
    const packageUrl = data.packageUrl || (rawDataAny.package_base_url as string) || (rawDataAny.package_url as string);
    const launchUrl = data.launchUrl || (rawDataAny.launch_url as string);
    const defaultOrganizationId = data.defaultOrganizationId || (rawDataAny.default_organization_id as string);

    const launchConfig: ScormLaunchConfig | null = useMemo(() => {
        // packageId may be null for preview-uploaded packages; only packageUrl + launchUrl are required
        if (!packageUrl || !launchUrl || !sessionId) {
            return null;
        }
        return {
            session_id: sessionId,
            content_id: '',
            package_id: packageId,
            package_base_url: packageUrl,
            launch_url: launchUrl,
            scorm_version: 'scorm-12',
            manifest: (data.manifest ?? {}) as any,
            default_organization_id: defaultOrganizationId,
            endpoints: {
                commit_url: `/api/content-sessions/${sessionId}/scorm/commit`,
                runtime_url: `/api/content-sessions/${sessionId}/scorm/runtime`,
                runtime_all_url: `/api/content-sessions/${sessionId}/scorm/runtime/all`,
                heartbeat_url: `/api/content-sessions/${sessionId}/heartbeat`,
                submit_url: `/api/content-sessions/${sessionId}/submit`,
                close_url: `/api/content-sessions/${sessionId}/close`,
            },
        };
    }, [packageId, packageUrl, launchUrl, defaultOrganizationId, data.manifest, sessionId]);

    const handleComplete = (event: ScormCompletionEvent) => {
        onAnswer?.({
            completed: true,
            scoreRaw: event.scoreRaw,
            completionStatus: event.completionStatus,
            totalTime: event.totalTime,
        });
    };

    if (!launchConfig || !sessionToken) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 300,
                backgroundColor: '#f9fafb',
                borderRadius: 8,
                color: '#6b7280',
                fontSize: 14,
            }}>
                {!packageUrl ? t.playerNoPackage : t.playerNoSession}
            </div>
        );
    }

    return (
        <div className="scorm-12-player" style={{ width: '100%' }}>
            <ScormPlayerFrame
                launchConfig={launchConfig}
                sessionToken={sessionToken}
                learnerName={learnerName}
                learnerId={learnerId}
                onComplete={handleComplete}
                height="600px"
                language={language}
            />
        </div>
    );
}
